import React, { useState, useEffect, useRef } from 'react';
import { FaUserPlus, FaSearch, FaTimes, FaCalendarAlt } from 'react-icons/fa';
import { collection, addDoc, updateDoc, doc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import styles from './InsuranceModals.module.css';
import { useTheme } from '../../contexts/ThemeContext';

const AddInsuranceModal = ({ show, handleClose, onInsuranceAdded, selectedInsurance, employees, employeeId }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    employeeId: '',
    hasBES: false,
    accidentInsurance: {
      startDate: '',
      endDate: '',
      cost: ''
    },
    complementaryHealth: {
      startDate: '',
      endDate: '',
      cost: ''
    },
    malpractice: {
      startDate: '',
      endDate: '',
      cost: ''
    },
    cost: '',
  });

  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [filter, setFilter] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [errors, setErrors] = useState({});

  // Calculate total cost automatically
  useEffect(() => {
    const accidentCost = parseFloat(formData.accidentInsurance.cost) || 0;
    const healthCost = parseFloat(formData.complementaryHealth.cost) || 0;
    const malpracticeCost = parseFloat(formData.malpractice.cost) || 0;

    const totalCost = accidentCost + healthCost + malpracticeCost;

    setFormData(prev => ({
      ...prev,
      cost: totalCost > 0 ? totalCost.toFixed(2) : ''
    }));
  }, [formData.accidentInsurance.cost, formData.complementaryHealth.cost, formData.malpractice.cost]);

  // Çalışan seçildiğinde veya sigorta düzenlendiğinde form verilerini yükle
  useEffect(() => {
    const loadInsuranceData = async () => {
      if (show) {
        if (selectedInsurance) {
          // Düzenleme modu - seçili sigortanın verilerini yükle
          setFormData({
            employeeId: selectedInsurance.employeeId,
            hasBES: selectedInsurance.hasBES,
            accidentInsurance: {
              startDate: selectedInsurance.accidentInsurance?.startDate || '',
              endDate: selectedInsurance.accidentInsurance?.endDate || '',
              cost: selectedInsurance.accidentInsurance?.cost || ''
            },
            complementaryHealth: {
              startDate: selectedInsurance.complementaryHealth?.startDate || '',
              endDate: selectedInsurance.complementaryHealth?.endDate || '',
              cost: selectedInsurance.complementaryHealth?.cost || ''
            },
            malpractice: {
              startDate: selectedInsurance.malpractice?.startDate || '',
              endDate: selectedInsurance.malpractice?.endDate || '',
              cost: selectedInsurance.malpractice?.cost || ''
            },
            cost: selectedInsurance.cost || '',
          });
          setSelectedEmployee(selectedInsurance.employeeId);
        } else if (employeeId) {
          // Çalışanlar sayfasından "Sigortalar" butonuyla açıldığında
          try {
            const insurancesRef = collection(db, 'Insurances');
            const q = query(insurancesRef, where("employeeId", "==", employeeId));
            const querySnapshot = await getDocs(q);
            
            if (querySnapshot.docs.length > 0) {
              const existingInsurance = querySnapshot.docs[0].data();
              setFormData({
                employeeId: employeeId,
                hasBES: existingInsurance.hasBES || false,
                accidentInsurance: {
                  startDate: existingInsurance.accidentInsurance?.startDate || '',
                  endDate: existingInsurance.accidentInsurance?.endDate || '',
                  cost: existingInsurance.accidentInsurance?.cost || ''
                },
                complementaryHealth: {
                  startDate: existingInsurance.complementaryHealth?.startDate || '',
                  endDate: existingInsurance.complementaryHealth?.endDate || '',
                  cost: existingInsurance.complementaryHealth?.cost || ''
                },
                malpractice: {
                  startDate: existingInsurance.malpractice?.startDate || '',
                  endDate: existingInsurance.malpractice?.endDate || '',
                  cost: existingInsurance.malpractice?.cost || ''
                },
                cost: existingInsurance.cost || '',
              });
            } else {
              // Çalışanın sigortası yoksa boş form
              setFormData({
                employeeId: employeeId,
                hasBES: false,
                accidentInsurance: { startDate: '', endDate: '', cost: '' },
                complementaryHealth: { startDate: '', endDate: '', cost: '' },
                malpractice: { startDate: '', endDate: '', cost: '' },
                cost: '',
              });
            }
            setSelectedEmployee(employeeId);
          } catch (error) {
            console.error('Sigorta bilgileri yüklenirken hata:', error);
          }
        } else {
          // "Sigorta Ekle" butonuyla açıldığında - tamamen boş form
          setFormData({
            employeeId: '',
            hasBES: false,
            accidentInsurance: { startDate: '', endDate: '', cost: '' },
            complementaryHealth: { startDate: '', endDate: '', cost: '' },
            malpractice: { startDate: '', endDate: '', cost: '' },
            cost: '',
          });
          setSelectedEmployee('');
        }
      }
    };

    loadInsuranceData();
  }, [show, selectedInsurance, employeeId]);

  const handleInputChange = (field, value) => {
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
    
    setFormData(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        };
      }
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handleEmployeeChange = (empId) => {
    setSelectedEmployee(empId);
    setFormData(prev => ({
      ...prev,
      employeeId: empId
    }));
    setDropdownOpen(false);
    
    // Clear employee error if it exists
    if (errors.employeeId) {
      setErrors({
        ...errors,
        employeeId: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedEmployee) {
      newErrors.employeeId = 'Lütfen bir çalışan seçiniz';
    }
    
    // Optional: validate other fields as needed
    if (formData.cost && isNaN(formData.cost)) {
      newErrors.cost = 'Maliyet sayısal bir değer olmalıdır';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Lütfen form hatalarını düzeltin');
      return;
    }
    
    try {
      const selectedEmployeeData = employees.find(emp => emp.id === selectedEmployee);
      const insuranceData = {
        ...formData,
        employeeId: selectedEmployee,
        employeeName: `${selectedEmployeeData?.eName} ${selectedEmployeeData?.eSurname}`,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (selectedInsurance) {
        await updateDoc(doc(db, 'Insurances', selectedInsurance.id), insuranceData);
        toast.success('Sigorta başarıyla güncellendi');
      } else {
        await addDoc(collection(db, 'Insurances'), insuranceData);
        toast.success('Sigorta başarıyla eklendi');
      }

      handleClose();
      if (onInsuranceAdded) onInsuranceAdded();
    } catch (error) {
      console.error('Error adding/updating insurance:', error);
      toast.error('Sigorta eklenirken veya güncellenirken bir hata oluştu');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false); // Dropdown dışında bir yere tıklanırsa kapat
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  if (!show) return null;

  // Filtrelenmiş çalışanlar - sadece aktif olanlar ve alfabetik sıralı
  const filteredEmployees = employees
    .filter(employee => employee.Active === 1) // Only active employees
    .filter(employee =>
      `${employee.eName} ${employee.eSurname}`.toLowerCase().includes(filter.toLowerCase())
    )
    .sort((a, b) => {
      const nameA = `${a.eName || ''} ${a.eSurname || ''}`.trim().toLowerCase();
      const nameB = `${b.eName || ''} ${b.eSurname || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB, 'tr');
    });

  return (
    <div className={styles.modalOverlay} onClick={(e) => {
      if (e.target === e.currentTarget) handleClose();
    }}>
      <div 
        className={`${styles.modalContainer} ${isDarkMode ? 'dark-mode' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaUserPlus className={styles.modalIcon} />
            {selectedInsurance ? 'Sigortayı Düzenle' : 'Sigorta Bilgileri'}
          </h2>
          <button 
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Kapat"
          >
            ×
          </button>
        </div>

        <div className={styles.modalContent}>
          {/* Çalışan Seçin Dropdown Menüsü - Sadece 'Sigorta Ekle' için */}
          {!selectedInsurance && !employeeId && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Çalışan Seçin:</label>
              <div className={styles.dropdownContainer} ref={dropdownRef}>
                <div className={styles.searchBar} style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Çalışan Seçin"
                    value={selectedEmployee ? employees.find(emp => emp.id === selectedEmployee)?.eName + ' ' + employees.find(emp => emp.id === selectedEmployee)?.eSurname : filter}
                    onChange={handleFilterChange}
                    onClick={() => setDropdownOpen(true)}
                    className={`${styles.formControl} ${errors.employeeId ? styles.formError : ''}`}
                  />
                  {selectedEmployee ? (
                    <button 
                      onClick={() => {
                        setSelectedEmployee('');
                        setFilter('');
                      }}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isDarkMode ? '#a0aec0' : '#718096'
                      }}
                    >
                      <FaTimes />
                    </button>
                  ) : (
                    <FaSearch style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: isDarkMode ? '#a0aec0' : '#718096'
                    }} />
                  )}
                </div>
                
                {errors.employeeId && (
                  <div className={styles.errorMessage}>{errors.employeeId}</div>
                )}
                
                {dropdownOpen && (
                  <div className={styles.dropdown}>
                    {filteredEmployees.length > 0 ? (
                      filteredEmployees.map(employee => (
                        <div
                          key={employee.id}
                          className={styles.dropdownItem}
                          onClick={() => handleEmployeeChange(employee.id)}
                        >
                          {employee.eName} {employee.eSurname}
                        </div>
                      ))
                    ) : (
                      <div className={styles.nothingFound}>
                        Çalışan bulunamadı
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>BES:</label>
            <select
              value={formData.hasBES.toString()}
              onChange={(e) => handleInputChange('hasBES', e.target.value === 'true')}
              className={`${styles.formControl} ${styles.selectControl}`}
            >
              <option value="false">Hayır</option>
              <option value="true">Evet</option>
            </select>
          </div>

          <div className={styles.sectionTitle}>Ferdi Kaza Sigortası</div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Başlangıç Tarihi:
            </label>
            <input
              type="date"
              value={formData.accidentInsurance.startDate}
              onChange={(e) => handleInputChange('accidentInsurance.startDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Bitiş Tarihi:
            </label>
            <input
              type="date"
              value={formData.accidentInsurance.endDate}
              onChange={(e) => handleInputChange('accidentInsurance.endDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Kişi Bazlı Maliyet (₺):</label>
            <input
              type="number"
              step="0.01"
              value={formData.accidentInsurance.cost}
              onChange={(e) => handleInputChange('accidentInsurance.cost', e.target.value)}
              placeholder="0.00"
              className={styles.formControl}
            />
          </div>

          <div className={styles.sectionTitle}>Tamamlayıcı Sağlık Sigortası</div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Başlangıç Tarihi:
            </label>
            <input
              type="date"
              value={formData.complementaryHealth.startDate}
              onChange={(e) => handleInputChange('complementaryHealth.startDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Bitiş Tarihi:
            </label>
            <input
              type="date"
              value={formData.complementaryHealth.endDate}
              onChange={(e) => handleInputChange('complementaryHealth.endDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Kişi Bazlı Maliyet (₺):</label>
            <input
              type="number"
              step="0.01"
              value={formData.complementaryHealth.cost}
              onChange={(e) => handleInputChange('complementaryHealth.cost', e.target.value)}
              placeholder="0.00"
              className={styles.formControl}
            />
          </div>

          <div className={styles.sectionTitle}>Malpraktis Sigortası</div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Başlangıç Tarihi:
            </label>
            <input
              type="date"
              value={formData.malpractice.startDate}
              onChange={(e) => handleInputChange('malpractice.startDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaCalendarAlt style={{ marginRight: '5px' }} /> Bitiş Tarihi:
            </label>
            <input
              type="date"
              value={formData.malpractice.endDate}
              onChange={(e) => handleInputChange('malpractice.endDate', e.target.value)}
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Kişi Bazlı Maliyet (₺):</label>
            <input
              type="number"
              step="0.01"
              value={formData.malpractice.cost}
              onChange={(e) => handleInputChange('malpractice.cost', e.target.value)}
              placeholder="0.00"
              className={styles.formControl}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Toplam Maliyet (₺):</label>
            <input
              type="text"
              value={formData.cost}
              readOnly
              placeholder="Otomatik hesaplanacak"
              className={`${styles.formControl} ${styles.readOnlyField}`}
              style={{ backgroundColor: isDarkMode ? '#2d3748' : '#f7fafc', cursor: 'not-allowed' }}
            />
          </div>

          <div className={styles.buttonGroup}>
            <button onClick={handleClose} className={styles.buttonCancel}>
              İptal
            </button>
            <button onClick={handleSubmit} className={styles.buttonSubmit}>
              {selectedInsurance ? 'Güncelle' : 'Kaydet'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInsuranceModal;